<script>
  import { onMount } from 'svelte';
  import { getApiUrl } from '../api';
  import { authStore } from '../authStore';

  let allQuestions = [];
  let selectedQuestionId = '';
  let audience = []; // Current page data
  
  // Pagination & Sort State
  let page = 1;
  const pageSize = 10;
  let total = 0;
  let totalPages = 0;
  let sortBy = 'answerTime'; // 'answerTime', 'displayName', 'answers'
  let sortOrder = 'desc';

  // Selection State
  let selectedUserIds = new Set();
  
  // Message Form State
  let subject = '';
  let body = '';
  let startDate = '';
  let expiryDate = '';
  let imageBase64 = '';
  let fileName = '';
  
  let isLoading = false;
  let error = '';
  let successMessage = '';
  let currentUser = null;
  let fileInput;

  onMount(async () => {
    const unsubscribe = authStore.subscribe(value => {
      currentUser = value.user;
    });
    fetchQuestions();
    return unsubscribe;
  });

  async function fetchQuestions() {
    isLoading = true;
    try {
      const response = await fetch(getApiUrl('/questions'));
      if (!response.ok) throw new Error('Could not load questions.');
      allQuestions = await response.json();
    } catch (e) {
      error = e.message;
    } finally {
      isLoading = false;
    }
  }
  
  // Reset and fetch when question changes
  $: if (selectedQuestionId) {
    page = 1;
    selectedUserIds = new Set(); // Clear selections on new question
    fetchAudience();
  }

  async function fetchAudience() {
    isLoading = true;
    error = '';
    audience = [];
    try {
      const url = getApiUrl(`/audience/${selectedQuestionId}?page=${page}&pageSize=${pageSize}&sortBy=${sortBy}&sortOrder=${sortOrder}`);
      const response = await fetch(url);
      if (!response.ok) throw new Error('Could not fetch audience.');
      
      const result = await response.json();
      audience = result.data || [];
      total = result.total || 0;
      totalPages = result.totalPages || 0;
      
    } catch (e) {
      error = e.message;
    } finally {
      isLoading = false;
    }
  }

  function handleSort(column) {
      if (sortBy === column) {
          sortOrder = sortOrder === 'asc' ? 'desc' : 'asc';
      } else {
          sortBy = column;
          sortOrder = 'asc'; // Default new sort to asc
      }
      fetchAudience();
  }

  function goToPage(newPage) {
      if (newPage >= 1 && newPage <= totalPages) {
          page = newPage;
          fetchAudience();
      }
  }

  // --- SELECTION LOGIC ---
  function toggleAllOnPage(e) {
      if (e.target.checked) {
          audience.forEach(m => selectedUserIds.add(m.userId));
      } else {
          audience.forEach(m => selectedUserIds.delete(m.userId));
      }
      selectedUserIds = selectedUserIds; // Trigger reactivity
  }

  function toggleRow(userId) {
      if (selectedUserIds.has(userId)) {
          selectedUserIds.delete(userId);
      } else {
          selectedUserIds.add(userId);
      }
      selectedUserIds = selectedUserIds; // Trigger reactivity
  }

  $: allOnPageSelected = audience.length > 0 && audience.every(m => selectedUserIds.has(m.userId));

  // --- IMAGE UPLOAD LOGIC ---
  function handleFileChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    
    // Check file size (e.g., max 2MB)
    if (file.size > 2 * 1024 * 1024) {
        alert("File size too large. Max 2MB.");
        return;
    }

    fileName = file.name;
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
        // Remove data URL prefix
        imageBase64 = reader.result.split(',')[1];
    };
  }

  async function handleSendMessage() {
    isLoading = true;
    error = '';
    successMessage = '';

    if (!subject || !body || !startDate || !expiryDate) {
        error = 'All message fields are required.';
        isLoading = false;
        return;
    }

    if (selectedUserIds.size === 0) {
        error = "Please select at least one member from the list to send the message to.";
        isLoading = false;
        return;
    }
    
    const recipientUserIds = Array.from(selectedUserIds);

    const payload = {
      recipientUserIds: recipientUserIds,
      message: {
        senderUserId: currentUser?.idTokenClaims?.oid,
        subject,
        body,
        startDate: new Date(startDate).toISOString(),
        expiryDate: new Date(expiryDate).toISOString(),
        imageBase64: imageBase64 // Send image data if present
      }
    };

    try {
      const response = await fetch(getApiUrl('/messages/send'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.error || 'Failed to send message.');
      }
      
      successMessage = `Message sent successfully to ${recipientUserIds.length} members!`;
      
      // Reset form
      subject = '';
      body = '';
      startDate = '';
      expiryDate = '';
      imageBase64 = '';
      fileName = '';
      if(fileInput) fileInput.value = '';
      selectedUserIds = new Set();

    } catch (e) {
      error = e.message;
    } finally {
      isLoading = false;
    }
  }

  function formatTime(iso) {
      if (!iso) return 'N/A';
      return new Date(iso).toLocaleString();
  }
</script>

<div class="audience-container">
  <h2>Target Audience Messaging</h2>

  <div class="selection-section">
    <label for="question-select">Select a Profile Question to Target:</label>
    <select id="question-select" bind:value={selectedQuestionId}>
      <option value="" disabled>-- Choose a question --</option>
      {#each allQuestions as q (q._id)}
        <option value={q._id}>{q.question}</option>
      {/each}
    </select>
  </div>

  {#if selectedQuestionId}
    <div class="audience-grid">
      
      <!-- Audience List Table -->
      <div class="audience-list">
        <h3>Audience Members (Found: {total})</h3>
        
        {#if isLoading && audience.length === 0}
            <p>Loading...</p>
        {:else if audience.length === 0}
            <p>No members found for this question.</p>
        {:else}
            <div class="table-wrapper">
            <table>
                <thead>
                <tr>
                    <th class="checkbox-col"><input type="checkbox" checked={allOnPageSelected} on:change={toggleAllOnPage}></th>
                    <th class="num-col">#</th>
                    <th class="sortable" on:click={() => handleSort('answerTime')}>
                        Answer Time {sortBy === 'answerTime' ? (sortOrder === 'asc' ? '▲' : '▼') : ''}
                    </th>
                    <th class="sortable" on:click={() => handleSort('displayName')}>
                        Member Name {sortBy === 'displayName' ? (sortOrder === 'asc' ? '▲' : '▼') : ''}
                    </th>
                    <th class="sortable" on:click={() => handleSort('answers')}>
                        Answer(s) {sortBy === 'answers' ? (sortOrder === 'asc' ? '▲' : '▼') : ''}
                    </th>
                </tr>
                </thead>
                <tbody>
                {#each audience as member, i (member.userId)}
                    <tr class:selected={selectedUserIds.has(member.userId)} on:click={() => toggleRow(member.userId)}>
                        <td><input type="checkbox" checked={selectedUserIds.has(member.userId)}></td>
                        <td>{(page - 1) * pageSize + i + 1}</td>
                        <td>{formatTime(member.lastUpdated)}</td>
                        <td>{member.displayName}</td>
                        <td>{member.answers.join(', ')}</td>
                    </tr>
                {/each}
                </tbody>
            </table>
            </div>

            <!-- Pagination Controls -->
            <div class="pagination">
                <button disabled={page <= 1} on:click={() => goToPage(page - 1)}>Previous</button>
                <span>Page {page} of {totalPages}</span>
                <button disabled={page >= totalPages} on:click={() => goToPage(page + 1)}>Next</button>
            </div>
        {/if}
      </div>

      <!-- Messaging Form -->
      <div class="message-form">
        <h3>Compose Message</h3>
        <p class="recipient-count">Recipients Selected: <strong>{selectedUserIds.size}</strong></p>
        
        <form on:submit|preventDefault={handleSendMessage}>
          <div class="form-group">
            <label for="subject">Subject</label>
            <input type="text" id="subject" bind:value={subject} required />
          </div>
          
          <div class="form-group">
            <label for="body">Body</label>
            <textarea id="body" rows="6" bind:value={body} required></textarea>
          </div>
          
          <!-- Image Upload -->
          <div class="form-group">
              <label for="msg-image">Attach Image (Optional)</label>
              <input 
                type="file" 
                id="msg-image" 
                accept="image/png, image/jpeg" 
                on:change={handleFileChange} 
                bind:this={fileInput}
              />
              {#if fileName}
                  <small class="file-name">Selected: {fileName}</small>
              {/if}
          </div>

          <div class="form-group">
            <label for="start-date">Start Date</label>
            <input type="datetime-local" id="start-date" bind:value={startDate} required />
          </div>
          
          <div class="form-group">
            <label for="expiry-date">Expiry Date</label>
            <input type="datetime-local" id="expiry-date" bind:value={expiryDate} required />
          </div>
          
          {#if error} <p class="error">{error}</p> {/if}
          {#if successMessage}<p class="success">{successMessage}</p>{/if}

          <button type="submit" disabled={isLoading || selectedUserIds.size === 0}>
            {#if isLoading}Sending...{:else}Send Message{/if}
          </button>
        </form>
      </div>
    </div>
  {/if}
</div>

<style>
  .audience-container { max-width: 1200px; margin: 2rem auto; padding: 0 1rem; }

  .selection-section { background: #fff; padding: 1.5rem; border-radius: 8px; margin-bottom: 2rem; box-shadow: 0 4px 6px rgba(0,0,0,0.05); }
  select { width: 100%; padding: 0.75rem; font-size: 1rem; border: 1px solid #ccc; border-radius: 4px; }

  .audience-grid { display: grid; grid-template-columns: 2fr 1fr; gap: 2rem; }
  @media(max-width: 900px) { .audience-grid { grid-template-columns: 1fr; } }

  .message-form, .audience-list { background: #fff; padding: 1.5rem; border-radius: 8px; box-shadow: 0 4px 6px rgba(0,0,0,0.05); }
  
  /* Table Styles */
  .table-wrapper { overflow-x: auto; margin-bottom: 1rem; }
  table { width: 100%; border-collapse: collapse; font-size: 0.95rem; }
  th, td { padding: 10px; text-align: left; border-bottom: 1px solid #eee; }
  th.sortable { cursor: pointer; user-select: none; }
  th.sortable:hover { background-color: #f1f1f1; }
  tr:hover { background-color: #f9f9f9; cursor: pointer; }
  tr.selected { background-color: #e3f2fd; }
  .checkbox-col, .num-col { width: 40px; text-align: center; }
  
  .pagination { display: flex; justify-content: space-between; align-items: center; padding-top: 1rem; border-top: 1px solid #eee; }
  .pagination button { padding: 0.5rem 1rem; border: 1px solid #ccc; background: white; border-radius: 4px; cursor: pointer; }
  .pagination button:disabled { opacity: 0.5; cursor: not-allowed; }

  /* Form Styles */
  .recipient-count { background: #e8f5e9; color: #2e7d32; padding: 0.5rem; border-radius: 4px; display: inline-block; margin-bottom: 1rem; }
  form { display: flex; flex-direction: column; gap: 1rem; }
  .form-group label { display: block; margin-bottom: 0.5rem; font-weight: 500; }
  input, textarea { width: 100%; padding: 0.75rem; border: 1px solid #ccc; border-radius: 4px; box-sizing: border-box; }
  button[type="submit"] { padding: 0.75rem; background-color: #007bff; color: white; border: none; border-radius: 4px; font-size: 1.1rem; cursor: pointer; }
  button:disabled { background-color: #aaa; }
  
  .error { color: red; }
  .success { color: green; }
  .file-name { color: #007bff; font-style: italic; }
</style>